import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  Customer,
  PatchAdminV1StoresStoreIdCustomersIdBody,
  PostAdminV1StoresStoreIdCustomersBody,
  PostAdminV1StoresStoreIdCustomersIdInviteBody,
} from '@nucleus-commerce/api-client/admin';

import { giftCardKeys } from '@/features/gift-cards';
import { orderKeys } from '@/features/orders';
import { privacyRequestKeys } from '@/features/privacy-requests';

import {
  customersApi,
  type AccountEmailKind,
  type CustomerPrivacyKind,
} from '../api/customers-api';

import { customerKeys } from './customer-keys';

function remember(
  queryClient: ReturnType<typeof useQueryClient>,
  storeId: string,
  customer: Customer,
) {
  queryClient.setQueryData(customerKeys.detail(storeId, customer.id), customer);
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: customerKeys.lists(storeId) }),
    queryClient.invalidateQueries({ queryKey: customerKeys.timeline(storeId, customer.id) }),
  ]);
}

/**
 * A merged-away or deleted customer answers 404 from now on. Its page may still be mounted until
 * the caller navigates, so the cache is only marked stale: a refetch now would trip the 404 boundary.
 */
function gone(queryClient: ReturnType<typeof useQueryClient>, storeId: string, id: string) {
  return queryClient.invalidateQueries({
    queryKey: customerKeys.detail(storeId, id),
    refetchType: 'none',
  });
}

/** 409 ALREADY_EXISTS on `body.email` lands on the email field. */
export function useCreateCustomer(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdCustomersBody) => customersApi.create(storeId, body),
    meta: { successMessage: 'customers:toasts.created', silentError: true },
    onSuccess: (customer) => remember(queryClient, storeId, customer),
  });
}

/** Tags and notes save quietly from the page; the dialogs pass their own toast. */
export function useUpdateCustomer(storeId: string, successMessage?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchAdminV1StoresStoreIdCustomersIdBody }) =>
      customersApi.update(storeId, id, body),
    meta: { successMessage, silentError: true },
    onSuccess: (customer) => remember(queryClient, storeId, customer),
  });
}

/**
 * The source's orders, gift cards and requests move onto the target and the source disappears,
 * so every list that could show either of them refetches.
 */
export function useMergeCustomers(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ targetId, sourceId }: { targetId: string; sourceId: string }) =>
      customersApi.merge(storeId, targetId, sourceId),
    meta: { successMessage: 'customers:toasts.merged', silentError: true },
    onSuccess: (customer, { sourceId }) => {
      queryClient.setQueryData(customerKeys.detail(storeId, customer.id), customer);
      return Promise.all([
        gone(queryClient, storeId, sourceId),
        queryClient.invalidateQueries({ queryKey: customerKeys.lists(storeId) }),
        queryClient.invalidateQueries({ queryKey: customerKeys.detail(storeId, customer.id) }),
        queryClient.invalidateQueries({ queryKey: orderKeys.all(storeId) }),
        queryClient.invalidateQueries({ queryKey: giftCardKeys.all(storeId) }),
        queryClient.invalidateQueries({ queryKey: privacyRequestKeys.all(storeId) }),
      ]);
    },
  });
}

/** The anonymising deletion: the record is gone and its orders are redacted. */
export function useDeleteCustomer(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => customersApi.remove(storeId, id),
    meta: { successMessage: 'customers:toasts.deleted', silentError: true },
    onSuccess: (_data, id) =>
      Promise.all([
        gone(queryClient, storeId, id),
        queryClient.invalidateQueries({ queryKey: customerKeys.lists(storeId) }),
        queryClient.invalidateQueries({ queryKey: orderKeys.all(storeId) }),
        queryClient.invalidateQueries({ queryKey: privacyRequestKeys.all(storeId) }),
      ]),
  });
}

/** Invitation or password reset; ORIGIN_NOT_ALLOWED on `body.resetUrl` belongs to the dialog. */
export function useSendAccountEmail(storeId: string, id: string, kind: AccountEmailKind) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdCustomersIdInviteBody) =>
      customersApi.sendAccountEmail(storeId, id, kind, body),
    meta: {
      successMessage:
        kind === 'invite' ? 'customers:toasts.inviteSent' : 'customers:toasts.resetSent',
      silentError: true,
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: customerKeys.timeline(storeId, id) }),
  });
}

/** 409 ALREADY_EXISTS on `body.kind` while a request of that kind is still open. */
export function useOpenCustomerPrivacyRequest(storeId: string, id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (kind: CustomerPrivacyKind) => customersApi.openPrivacyRequest(storeId, id, kind),
    meta: { successMessage: 'customers:toasts.privacyRequestOpened', silentError: true },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: privacyRequestKeys.all(storeId) }),
        queryClient.invalidateQueries({ queryKey: customerKeys.timeline(storeId, id) }),
      ]),
  });
}
